<template>
  <div class="brand-dashboard container-fluid">
    <!-- Brand Settings -->
    <div class="card mb-3 brand-card">
      <div class="card-body">
        <div class="d-flex align-items-center mb-3">
          <div class="icon-circle mr-2">
            <span class="icon-inner">
              <Store width="20" />️
            </span>
          </div>
          <h5 class="card-title mb-0">
            Brand Settings
          </h5>
        </div>

        <div class="form-group mb-2">
          <label class="mb-1">Brand Name</label>

          <div class="d-flex align-items-center">
            <Multiselect
              v-model="selectedBrand"
              :options="brands"
              :multiple="false"
              :searchable="true"
              track-by="id"
              label="brand_name"
              placeholder="Choose brand..."
              :allow-empty="false"
              open-direction="auto"
            />

            <!-- Add brand button -->
            <button
              class="btn btn-teal ml-2"
              type="button"
              @click="toggleAddBrand"
            >
              +
            </button>
            <button
              v-if="selectedBrand"
              class="btn btn-danger ml-2"
              type="button"
              @click="deleteBrand"
            >
              x
            </button>
          </div>
        </div>

        <!-- Add new brand field -->
        <div
          v-if="addingBrand"
          class="mt-2 pl-1"
        >
          <div class="d-flex align-items-center">
            <input
              v-model="newBrand"
              class="form-control mr-2"
              type="text"
              placeholder="Enter new brand..."
            >
            <button
              class="btn btn-success mr-2"
              @click="saveBrand"
            >
              Save
            </button>
            <button
              class="btn btn-outline-secondary"
              @click="cancelAddBrand"
            >
              Cancel
            </button>
          </div>
        </div>

        <small class="text-muted d-block mt-2">
          Share of Shelf calculates how many products from this brand appear in top search results
        </small>
      </div>
    </div>

    <!-- Overall Share of Shelf -->
    <div class="card mb-3">
      <div class="card-body">
        <div class="d-flex align-items-center mb-3">
          <div class="icon-circle mr-2">
            <span class="icon-inner">
              <TrendingUp width="20" />
            </span>
          </div>
          <h5 class="card-title mb-0">
            Overall Share of Shelf
          </h5>
        </div>

        <div class="d-flex justify-content-between align-items-baseline mb-2">
          <div class="overall-share-main">
            <div class="overall-share-value">
              {{ overAllShare.current }}/{{ overAllShare.total }}
            </div>
          </div>
          <div class="overall-share-percentage text-right">
            <div class="percentage-value">
              {{ overAllShare.percent }}%
            </div>
          </div>
        </div>

        <div class="progress progress-thin mb-2">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: overAllShare.percent + '%' }"
          />
        </div>

        <small class="text-muted d-block">
          Across {{ overAllShare.keywords }} tracked keywords, <strong style="color: #0b1220">{{ selectedBrand?.brand_name ?? '' }}</strong>
          appears in {{ overAllShare.current }} out of {{ overAllShare.total }} top search positions
        </small>
      </div>
    </div>

    <!-- Track Keywords -->
    <div 
      v-if="selectedBrand" 
      class="card mb-3"
    >
      <div class="card-body">
        <h5 class="card-title mb-3">
          Track Keywords
        </h5>
        <div class="d-flex">
          <input
            v-model="newKeyword"
            type="text"
            class="form-control mr-2"
            placeholder="Enter keyword to track..."
            @keyup.enter="addKeyword"
          >
          <button
            class="btn btn-teal"
            type="button"
            @click="addKeyword"
          >
            +
          </button>
        </div>
      </div>
    </div>

    <!-- Keyword Breakdown -->
    <div 
      v-if="selectedBrand" 
      class="card mb-3"
    >
      <div class="card-body">
        <h5 class="card-title mb-3">
          Keyword Breakdown
        </h5>

        <div class="table-responsive">
          <table class="table keyword-table mb-0">
            <thead>
              <tr>
                <th>Keyword</th>
                <th class="text-right">
                  Share
                </th>
                <th class="text-center">
                  Percentage
                </th>
                <th>Visual</th>
                <th
                  class="text-center"
                  style="width: 100px;"
                />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in selectedBrand.keywords"
                :key="row.id"
              >
                <td class="keyword-cell">
                  {{ row.keyword }}
                </td>
                <td class="text-right">
                  <strong>{{ row.found_results ?? '-' }}</strong>/{{ row.total_results ?? '-' }}
                </td>
                <td class="text-center text-teal">
                  <span v-if="row.found_results && row.total_results">{{ (row.found_results / row.total_results * 100).toFixed(1) }}%</span>
                  <span v-else>-</span>
                </td>
                <td>
                  <div class="progress progress-thin">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="{ width: (row.found_results / row.total_results * 100).toFixed(0) + '%' }"
                    />
                  </div>
                </td>
                <td class="text-center">
                  <button
                    class="btn btn-link btn-sm text-muted p-0"
                    type="button"
                    @click="showInfo(row)"
                  >
                    <Info width="14" />
                  </button>
                  <button 
                    class="btn btn-link btn-sm text-muted p-0 ml-2" 
                    type="button"
                    @click="refreshKeyword(row)"
                  >
                    <RefreshCw width="14" />
                  </button>
                  <button
                    class="btn btn-link btn-sm text-muted p-0 ml-2"
                    type="button"
                    @click="removeKeyword(row)"
                  >
                    <Trash width="14" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <ShareOfShelfKeywordModal
      v-model="showStats"
      :keyword-id="selectedKeyword"
    />
  </div>
</template>

<script>
import toastr from 'toastr';
import { Store, TrendingUp, RefreshCw, Info, Trash } from 'lucide-vue';
import Multiselect from 'vue-multiselect';
import ShareOfShelfKeywordModal from "@frontend/components/common/dashboard/modals/ShareOfShelfKeywordModal.vue";
export default {
  name: 'ShareOfShelf',
  components: { 
    Store,
    TrendingUp,
    Multiselect,
    RefreshCw,
    ShareOfShelfKeywordModal,
    Info,
    Trash
  },
  data() {
    return {
      brands: [],
      selectedBrand: null,
      addingBrand: false,
      newBrand: '',
      overallShare: {
        current: 17,
        total: 80,
        percent: 21.3,
        keywords: 4,
      },
      newKeyword: '',
      showStats: false,
      chart: {
        labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
        totalCounts: [10, 20, 30, 25, 20, 30, 45],
        matchCounts: [15, 25, 20, 35, 30, 40, 35],
      },
      titles: [],
      selectedKeyword: 0
    };
  },
  computed: {
    overAllShare: function () {
      if (!this.selectedBrand) {
        return {
          current: '-',
          total: '-',
          percent: 0,
          keywords: '-',
        }
      }

      let keywords = this.selectedBrand.keywords.length;
      let current = 0;
      let total = 0;
      this.selectedBrand.keywords.forEach(item => {
        if (!item.found_results || !item.total_results || item.found_results === '-' || item.total_results === '-') return;
        current += item.found_results;
        total += item.total_results;
      })
      
      if (current && keywords) {
        current = (current / keywords).toFixed(0);
      } else {
        current = '-'
      }
      
      if (total && keywords) {
        total = (total / keywords).toFixed(0);
      } else {
        total = '-'
      }
      let percent = 0;
      if (current !== '-') {
        percent = (current / total * 100).toFixed(1)
      }

      return {
        current,
        total,
        percent,
        keywords
      }
    }
  },
  created() {
    if (window.currentUserCompanyId) {
      window.Echo.channel(`share_of_shelf.${window.currentUserCompanyId}`)
        .listen('.shelf.share.completed', (e) => {
          this.updateKeyword(e.data)
        });
    }
  },
  mounted() {
    this.getBrands();
  },
  methods: {
    showInfo(keyword) {
      this.showStats = true;
      this.selectedKeyword = keyword.id;
    },
    async getBrands() {
      const {data} = await axios.get('/share-of-shelf/get-brands');
      this.brands = data;
      
      if(this.brands.length > 0) this.selectedBrand = JSON.parse(JSON.stringify(this.brands[0]));
    },
    toggleAddBrand() {
      this.addingBrand = true;
      this.newBrand = '';
    },
    async saveBrand() {
      const name = this.newBrand.trim();
      if (!name) return;

      // prevent duplicates
      if (this.brands.find(item => item.brand_name === name)) {
        return;
      }
      
      const {data} = await axios.post('/share-of-shelf/create-brand', {brand_name: name});
      this.brands.push(data);

      this.selectedBrand = JSON.parse(JSON.stringify(data));
      this.addingBrand = false;
      this.newBrand = '';
    },
    cancelAddBrand() {
      this.addingBrand = false;
      this.newBrand = '';
    },
    refreshKeyword(keyword) {
      axios.get('/share-of-shelf/refresh-keyword/' + keyword.id);
      
      toastr.success('Keyword refresh request sent successfully.');
    },
    async addKeyword() {
      const trimmed = this.newKeyword.trim();
      if (!trimmed) return;
      
      if (this.selectedBrand?.keywords?.find(item => item.keyword === trimmed)) {
        return;
      }

      const {data} = await axios.post('/share-of-shelf/create-brand-keyword/' + this.selectedBrand.id, {keyword: trimmed});
      
      this.brands.forEach(brand => {
        if (+brand.id === +this.selectedBrand.id) brand.keywords.push(data);
      })
      
      this.selectedBrand.keywords.push(data);
      this.newKeyword = '';
    },
    updateKeyword(keyword) {
      this.brands.forEach(brand => {
        if (+brand.id === +keyword.share_of_shelf_brand_id) {
          brand.keywords.forEach(item => {
            if (+item.id === +keyword.id) {
              item.keyword = keyword.keyword;
              item.found_results = keyword.found_results;
              item.total_results = keyword.total_results;
            }
          });
          
          if (+brand.id === +this.selectedBrand.id) {
            this.selectedBrand = JSON.parse(JSON.stringify(brand));
          }
        }
      })
    },
    async removeKeyword(keyword) {
      const result = await Swal.fire({
        title: 'Delete Keyword?',
        html: `Are you sure you want to delete <strong>${keyword.keyword}</strong>? <br>This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      })

      if (!result.isConfirmed) {
        return
      }

      try {
        const {data} = await axios.delete('/share-of-shelf/delete-brand-keyword/' + this.selectedBrand.id + '/' + keyword.id);
        this.selectedBrand.keywords = this.selectedBrand.keywords.filter(item => item.id !== keyword.id);
        this.brands.forEach(brand => {
          if (brand.keywords && brand.keywords.length > 0) brand.keywords = brand.keywords.filter(item => item.id !== keyword.id)
        })
        toastr.success('Keyword deleted successfully.');
      } catch (e) {
        toastr.error(e.response.data.message ?? 'Something went wrong.');
      }
    },
    async deleteBrand() {
      const result = await Swal.fire({
        title: 'Delete Brand?',
        html: `Are you sure you want to delete <strong>${this.selectedBrand.brand_name}</strong> and all keywords for this brand? <br>This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      })

      if (!result.isConfirmed) {
        return
      }
      try {
        await axios.delete('/share-of-shelf/delete-brand/' + this.selectedBrand.id);
        this.brands = this.brands.filter(item => item.id !== this.selectedBrand.id);
        this.selectedBrand = this.brands.length ? JSON.parse(JSON.stringify(this.brands[0])) : null;
        toastr.success('Brand deleted successfully.');
      } catch (e) {
        toastr.error(e.response.data.message ?? 'Something went wrong.');
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.brand-dashboard {
  padding: 0;
  margin: 0;

  .card {
    border-radius: 8px;
    border: 1px solid #e6ecf1;
    box-shadow: 0 2px 4px rgba(15, 35, 52, 0.03);

    .card-body {
      padding: 1.25rem 1.5rem;
      background-color: transparent;
    }

    .card-title {
      font-size: 0.95rem;
      font-weight: 600;
      color: #223041;
    }
  }
}

.icon-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f2fbf8;
  display: flex;
  align-items: center;
  justify-content: center;

  .icon-inner {
    font-size: 14px;
  }
}

.overall-share-main .overall-share-value {
  font-size: 1.7rem;
  font-weight: 600;
  color: #101828;
}

.overall-share-percentage .percentage-value {
  font-size: 1rem;
  font-weight: 600;
  color: #16a085;
}

.progress-thin {
  height: 6px;
  border-radius: 999px;
  background-color: #f1f4f8;
  overflow: hidden;
  margin-bottom: 0;

  .progress-bar {
    background-color: #1abc9c;
  }
}

.keyword-table {
  thead th {
    border-top: none;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8792a2;
    font-weight: 600;
  }

  tbody tr:first-child td {
    border-top: 1px solid #e6ecf1;
  }

  tbody td {
    vertical-align: middle;
    font-size: 0.875rem;
  }

  .keyword-cell {
    color: #223041;
  }
}

.text-teal {
  color: #1abc9c !important;
}

.btn-teal {
  background-color: #1abc9c;
  border-color: #1abc9c;
  color: #fff;
  border-radius: 4px;
  padding: 0 0.9rem;
  font-size: 1.1rem;
  line-height: 1.1;
}

.btn-teal:hover {
  background-color: darken(#1abc9c, 5%);
  border-color: darken(#1abc9c, 5%);
}
.brand-card {
  .btn-teal {
    height: 38px;
    width: 38px;
  }
  
  .btn-danger {
    height: 38px;
    width: 38px;
  }
}
.highlighted {
  background: #ffef9f;
  color: #000;
  padding: 2px 3px;
  border-radius: 3px;
}
</style>
